import React from 'react';
import { Modal, Button, Descriptions, Image, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';

const { Text } = Typography;

const CustomizationInfoModal = ({ visible, onCancel, selectedCustomization }) => {
    // Helper function to extract customer customization inputs from complex JSON
    const extractCustomerInputs = (customizationData) => {
        const inputs = [];

        // Extract from version3.0 structure (cleaner format)
        if (customizationData['version3.0']?.customizationInfo?.surfaces) {
            customizationData['version3.0'].customizationInfo.surfaces.forEach(surface => {
                if (surface.areas) {
                    surface.areas.forEach(area => {
                        // Handle text inputs
                        if (area.text && area.text.trim() !== '') {
                            inputs.push({
                                label: area.label || area.name,
                                type: area.customizationType,
                                value: area.text,
                                priceDelta: area.priceDelta?.value || 0,
                                image: area.optionImage
                            });
                        }
                        // Handle option selections
                        else if (area.optionValue && area.optionValue.trim() !== '') {
                            inputs.push({
                                label: area.label || area.name,
                                type: area.customizationType,
                                value: area.optionValue,
                                priceDelta: area.priceDelta?.value || 0,
                                image: area.optionImage
                            });
                        }
                        // Handle image uploads
                        else if (area.customizationType === 'ImagePrinting' && area.svgImage) {
                            inputs.push({
                                label: area.label || area.name,
                                type: area.customizationType,
                                value: 'Custom Image Uploaded',
                                priceDelta: area.priceDelta?.value || 0,
                                isImageUpload: true,
                                svgImage: area.svgImage,
                                dimensions: area.Dimensions || area.dimensions
                            });
                        }
                    });
                }
            });
        }

        // Fallback to original customizationData structure
        if (inputs.length === 0 && customizationData.customizationData) {
            const extractFromChildren = (children) => {
                if (!children) return;

                children.forEach(child => {
                    // Handle text inputs
                    if (child.type === 'TextCustomization' && child.inputValue && child.inputValue.trim() !== '') {
                        inputs.push({
                            label: child.label,
                            type: 'TextPrinting',
                            value: child.inputValue,
                            priceDelta: 0
                        });
                    }

                    // Handle option selections
                    if (child.type === 'OptionCustomization' && child.optionSelection) {
                        inputs.push({
                            label: child.label,
                            type: 'Options',
                            value: child.optionSelection.name || child.optionSelection.label,
                            priceDelta: child.optionSelection.additionalCost?.amount || 0,
                            image: child.optionSelection.overlayImage?.imageUrl || child.optionSelection.thumbnailImage?.imageUrl
                        });
                    }

                    // Handle image uploads
                    if (child.type === 'ImageCustomization' && child.image) {
                        inputs.push({
                            label: child.label,
                            type: 'ImagePrinting',
                            value: child.image.buyerFilename || 'Custom Image Uploaded',
                            priceDelta: 0,
                            isImageUpload: true,
                            imageName: child.image.imageName
                        });
                    }

                    // Handle containers with price deltas
                    if (child.type === 'FlatRatePriceDeltaContainerCustomization' && child.children) {
                        child.children.forEach(subChild => {
                            if (subChild.type === 'TextCustomization' && subChild.inputValue && subChild.inputValue.trim() !== '') {
                                inputs.push({
                                    label: subChild.label,
                                    type: 'TextPrinting',
                                    value: subChild.inputValue,
                                    priceDelta: child.priceDelta?.amount || 0
                                });
                            }
                        });
                    }

                    // Handle placement containers (image uploads)
                    if (child.type === 'PlacementContainerCustomization' && child.children) {
                        child.children.forEach(subChild => {
                            if (subChild.type === 'ImageCustomization' && subChild.image) {
                                inputs.push({
                                    label: subChild.label,
                                    type: 'ImagePrinting',
                                    value: subChild.image.buyerFilename || 'Custom Image Uploaded',
                                    priceDelta: 0,
                                    isImageUpload: true,
                                    imageName: subChild.image.imageName,
                                    dimensions: child.dimension,
                                    position: child.position
                                });
                            }
                        });
                    }

                    if (child.children) {
                        extractFromChildren(child.children);
                    }
                });
            };

            if (customizationData.customizationData.children) {
                extractFromChildren(customizationData.customizationData.children);
            }
        }

        // Font and color selections are only available in the detailed tree,
        // so supplement them even when version3.0 already supplied other inputs.
        const existingInputs = new Set(inputs.map(input => JSON.stringify([input.label, input.value])));
        const extractFontAndColor = (children = []) => {
            children.forEach(child => {
                let label;
                let value;
                if (child.type === 'FontCustomization') {
                    label = child.label || child.name || 'Font Style';
                    value = child.fontSelection?.family?.trim();
                } else if (child.type === 'ColorCustomization') {
                    label = child.label || child.name || 'Font Color';
                    const name = child.colorSelection?.name?.trim();
                    const color = child.colorSelection?.value?.trim();
                    value = name && color ? `${name} (${color})` : name || color;
                }

                if (value) {
                    const key = JSON.stringify([label, value]);
                    if (!existingInputs.has(key)) {
                        inputs.push({ label, type: child.type, value, priceDelta: 0 });
                        existingInputs.add(key);
                    }
                }
                if (child.children) {
                    extractFontAndColor(child.children);
                }
            });
        };
        extractFontAndColor(customizationData.customizationData?.children);

        return inputs;
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <EditOutlined style={{ color: '#1890ff' }} />
                    <span>Customer Customization</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="close" type="primary" onClick={onCancel}>
                    Close
                </Button>
            ]}
            width={800}
            styles={{
                body: {
                    padding: '24px'
                }
            }}
        >
            {selectedCustomization && (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {(() => {
                        const customerInputs = extractCustomerInputs(selectedCustomization);

                        if (customerInputs.length === 0) {
                            return (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <EditOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                    <div>No customization details available</div>
                                </div>
                            );
                        }

                        return (
                            <Descriptions
                                bordered
                                column={1}
                                size="middle"
                                styles={{
                                    label: {
                                        width: '250px',
                                        backgroundColor: '#f8f9fa',
                                        fontWeight: 500,
                                        fontSize: '14px'
                                    },
                                    content: {
                                        backgroundColor: '#fff',
                                        padding: '16px',
                                        fontSize: '14px'
                                    }
                                }}
                            >
                                {customerInputs.map((input, idx) => (
                                    <Descriptions.Item key={idx} label={input.label}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            {input.image ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <Image
                                                        width={60}
                                                        height={60}
                                                        src={input.image}
                                                        style={{ borderRadius: '6px', border: '1px solid #d9d9d9' }}
                                                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
                                                    />
                                                    <Text strong>{input.value}</Text>
                                                </div>
                                            ) : (
                                                <Text strong style={{ fontSize: '14px' }}>{input.value}</Text>
                                            )}

                                            {input.priceDelta !== 0 && (
                                                <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                                                    ({input.priceDelta > 0 ? `+$${input.priceDelta}` : `$${input.priceDelta}`})
                                                </Text>
                                            )}
                                        </div>
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        );
                    })()}
                </div>
            )}
        </Modal>
    );
};

export default CustomizationInfoModal;
